import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ProfileField } from '../../../../types/Profile';
import { IsValidField } from '../../../../utils/validationUtils';
import useDelayedEffect from '../../../../hooks/effects/useDelayedEffect';

interface BirthDateInputProps {
  field: ProfileField;
  setField: (field: ProfileField) => void;
  canBeEmpty?: boolean;
  mandatory?: boolean;
  validate?: boolean;
}

export default function BirthDateInput({
  field,
  setField,
  canBeEmpty = false,
  mandatory = false,
  validate = false,
}: BirthDateInputProps): JSX.Element {
  const [day, month, year] = field?.birthDate?.split('-') || ['', '', ''];
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();

  const dayRef = React.useRef<HTMLInputElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const yearRef = React.useRef<HTMLInputElement>(null);

  useDelayedEffect(() => {
    if (!validate) return;
    if (!year && !month && !day) {
      setError(canBeEmpty ? '' : t('invalid.empty'));
      return;
    }
    if (!IsValidField(field, canBeEmpty)) {
      setError(t('invalid.invalid'));
      return;
    }
    setError('');
  }, [field.birthDate, validate]);

  useEffect(() => {
    if (!year && !month && !day && field.birthDate) {
      setField({ ...field, birthDate: '' });
    }
  }, [field, year, month, day]);

  return (
    <div className="relative flex w-fit flex-col">
      <label htmlFor={field.id} className="mb-1 pr-2 font-medium">
        {t('dataType.birthDate')} {mandatory ? '*' : ''}
      </label>
      <div className="flex w-full flex-row items-center gap-2">
        <input
          name="bday-day"
          id={field.id}
          type="text"
          className={classNames(
            'mr-1 flex h-10 w-[45px] items-center rounded-lg border border-gray-400 px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
            {
              '!border-danger': error,
            },
          )}
          placeholder="DD"
          data-testid={`suffix-input-${field.id}`}
          value={day || ''}
          ref={dayRef}
          onChange={(e) => {
            let value = e.target.value.replace(/[^0-9]/g, '');
            if (value.length > 2) {
              value = value.slice(0, 2);
            }
            setField({ ...field, birthDate: `${value}-${month || ''}-${year || ''}` });
            if (value.length === 2 && monthRef.current) {
              monthRef.current.focus();
            }
          }}
          onBlur={(e) => {
            if (e.target.value.length === 1) {
              const value = `0${e.target.value}`;
              setField({ ...field, birthDate: `${value}-${month || ''}-${year || ''}` });
            }
          }}
        />
        -
        <input
          name="bday-month"
          id={field.id}
          type="text"
          className={classNames(
            'mr-1 flex h-10 w-[45px] items-center rounded-lg border border-gray-400 px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
            {
              '!border-danger': error,
            },
          )}
          placeholder="MM"
          data-testid={`suffix-input-${field.id}`}
          ref={monthRef}
          value={month || ''}
          onChange={(e) => {
            let value = e.target.value.replace(/[^0-9]/g, '');
            if (value.length > 2) {
              value = value.slice(0, 2);
            }
            setField({ ...field, birthDate: `${day || ''}-${value}-${year || ''}` });
            if (value.length === 2 && yearRef.current) {
              yearRef.current.focus();
            }
          }}
          onBlur={(e) => {
            if (e.target.value.length === 1) {
              const value = `0${e.target.value}`;
              setField({ ...field, birthDate: `${day || ''}-${value}-${year || ''}` });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !e.currentTarget.value && dayRef.current) {
              dayRef.current.focus();
            }
          }}
        />
        -
        <input
          name="bday-year"
          id={field.id}
          type="text"
          className={classNames(
            'mr-1 flex h-10 w-[65px] items-center rounded-lg border border-gray-400 px-2 text-gray-600 focus:ring-0 focus:ring-transparent',
            {
              '!border-danger': error,
            },
          )}
          placeholder="YYYY"
          data-testid={`suffix-input-${field.id}`}
          ref={yearRef}
          value={year || ''}
          onChange={(e) => {
            let value = e.target.value.replace(/[^0-9]/g, '');
            if (value.length > 4) {
              value = value.slice(0, 4);
            }
            setField({ ...field, birthDate: `${day || ''}-${month || ''}-${value}` });
          }}
          onBlur={(e) => {
            if (e.target.value.length === 1) {
              const value = `0${e.target.value}`;
              setField({ ...field, birthDate: `${day || ''}-${month || ''}-${value}` });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !e.currentTarget.value && monthRef.current) {
              monthRef.current.focus();
            }
          }}
        />
      </div>
      <p className="absolute right-1 top-0 text-sm text-danger transition-all">{error}</p>
    </div>
  );
}
